/* eslint-disable react/prop-types */
import { Box, Button, Chip, DialogTitle, IconButton, Stack, Typography } from '@mui/material';
import { Iconify } from 'src/components';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { formatUkPhone } from 'src/utils/ukPhone';
import { stageOf } from 'src/components/callPill/callPillStage';
import { ACTION_TEXT, callTitle, metaParts, primaryAction } from './callReviewModel';
import CallBackButton from './CallBackButton';

const ACTION_ICON = {
  call_back: 'solar:phone-calling-bold',
  create_lead: 'solar:user-plus-bold',
  open_patient: 'solar:user-id-bold',
  stage_action: 'solar:clipboard-check-bold',
};

// Caller (or UK number + "Not a lead yet"), one meta line, and one primary action by state
// plus "Link to existing" (the same names as the Call Logs row buttons).
export default function CallReviewHeader({ call, practiceId, onClose, onFindContact, onCreateLead, onCallBack, onStageAction }) {
  const title = callTitle(call);
  const leadUrl =
    call?.lead?.id && practiceId
      ? PATH_DASHBOARD.general.leadDetail
          .replace(':practiceId', practiceId)
          .replace(':id', call.lead.id)
          .replace('/:tab?', '')
      : null;
  const primary = primaryAction(call);
  // What this patient's own Active Leads table would offer for their stage:
  // Booked -> Record Attended, Attended / FTA -> Follow up, and so on. Null for
  // a caller who is not a patient yet, or a stage with no screen of its own
  // (treatment started, closed, archived), which keeps plain "Open patient".
  const stage = stageOf({ leadId: call?.lead?.id, status: call?.lead?.status, source: 'call-review' });
  // An action this screen can't run (no handler / no lead page) falls back to the next one.
  const runnable = {
    call_back: onCallBack ? () => onCallBack(call) : null,
    create_lead: !call?.lead && onCreateLead ? () => onCreateLead(call) : null,
    open_patient: leadUrl ? 'href' : null,
    stage_action: stage?.inPopup && onStageAction ? () => onStageAction(stage) : null,
  };
  // A call-back still owed stays the main button, as it is on the row. Otherwise
  // the patient's stage action beats a plain "Open patient".
  const order = [primary, 'stage_action', 'open_patient', 'create_lead'];
  if (primary !== 'call_back') order.unshift('stage_action');
  const chosen = order.find((key) => runnable[key]);
  return (
    <DialogTitle id="call-review-title" sx={{ pb: 2, flexShrink: 0 }}>
      <Stack direction="row" alignItems="flex-start" spacing={2}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="h5" component="span" sx={{ overflowWrap: 'anywhere' }}>
              {title.text}
            </Typography>
            {!title.isLead && call && <Chip size="small" label="Not a lead yet" variant="outlined" />}
          </Stack>
          {title.isLead && call?.contact && (
            <Typography variant="body2" color="text.secondary">
              {call.numberDisplay || formatUkPhone(call.contact)}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" data-testid="call-review-meta" sx={{ mt: 0.5 }}>
            {metaParts(call).join(' · ')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap justifyContent="flex-end">
          {!call?.lead && onFindContact && (
            <Button variant="outlined" onClick={() => onFindContact(call)} sx={{ minHeight: 44 }}>
              Link to existing
            </Button>
          )}
          {call?.lead && onFindContact && (
            <Button onClick={() => onFindContact(call)} sx={{ minHeight: 44 }}>
              Change contact
            </Button>
          )}
          {/* The stage action takes the main button, so the patient page keeps a
              button of its own rather than disappearing behind it. */}
          {chosen === 'stage_action' && leadUrl && (
            <Button variant="outlined" component="a" href={leadUrl} sx={{ minHeight: 44 }}>
              {ACTION_TEXT.open_patient}
            </Button>
          )}
          {chosen === 'stage_action' && (
            <Button variant="contained" onClick={runnable.stage_action} startIcon={<Iconify icon={ACTION_ICON.stage_action} />} sx={{ minHeight: 44 }}>
              {stage.label}
            </Button>
          )}
          {chosen === 'open_patient' && (
            <Button variant="contained" component="a" href={leadUrl} startIcon={<Iconify icon={ACTION_ICON.open_patient} />} sx={{ minHeight: 44 }}>
              {ACTION_TEXT.open_patient}
            </Button>
          )}
          {chosen === 'call_back' && <CallBackButton label={ACTION_TEXT.call_back} onClick={runnable.call_back} />}
          {chosen && chosen !== 'open_patient' && chosen !== 'stage_action' && chosen !== 'call_back' && (
            <Button variant="contained" onClick={runnable[chosen]} startIcon={<Iconify icon={ACTION_ICON[chosen]} />} sx={{ minHeight: 44 }}>
              {ACTION_TEXT[chosen]}
            </Button>
          )}
          <IconButton aria-label="Close call review" onClick={onClose} sx={{ width: 44, height: 44 }}>
            <Iconify icon="eva:close-fill" />
          </IconButton>
        </Stack>
      </Stack>
    </DialogTitle>
  );
}
